"use client"

import { useState } from "react"
import { cn } from "@/lib/utils"
import { motion } from "@/components/insurance/static-motion"
import { useInsuranceTranslation } from "@/lib/insurance/use-insurance-translation"
import { formatCHF } from "@/lib/insurance/insurers"
import type { Addon } from "@/lib/insurance/addons"

const EASE_OUT_QUART = [0.25, 1, 0.5, 1] as const
const COLLAPSED_COUNT = 6

interface AddonSelectorProps {
  addons: Addon[]
  selectedIds: Set<string>
  onToggle: (addon: Addon) => void
}

export function AddonSelector({
  addons,
  selectedIds,
  onToggle,
}: AddonSelectorProps) {
  const { t } = useInsuranceTranslation()
  const [expanded, setExpanded] = useState(false)

  if (addons.length === 0) return null

  const needsCollapse = addons.length > COLLAPSED_COUNT
  const visible = expanded ? addons : addons.slice(0, COLLAPSED_COUNT)
  const hiddenCount = addons.length - COLLAPSED_COUNT
  // A membership earns the member rate on add-ons too (Stripe carries a
  // `member_price` for every one of them), so `getAddonsForService` hands back
  // member figures when a tier is selected. One line above the grid says so
  // instead of repeating a label on all thirteen chips.
  const memberPriced = addons.some((addon) => addon.listPrice > addon.price)

  return (
    <div>
      {memberPriced && (
        <p className="addon-member-note">{t("gate.addons.member_pricing")}</p>
      )}
      <div className="addon-grid">
        {visible.map((addon, i) => {
          const isSelected = selectedIds.has(addon.id)
          // Descriptions are keyed by the catalogue's humanReadableId. The
          // catalogue is remote and can grow, so an add-on without copy yet
          // renders name + price only rather than the raw key.
          const descKey = `addon.desc.${addon.humanReadableId}`
          const desc = t(descKey)
          const hasDesc = desc !== descKey
          return (
            <motion.button
              key={addon.id}
              type="button"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: i * 0.03,
                duration: 0.25,
                ease: EASE_OUT_QUART,
              }}
              className={cn("addon-chip", isSelected && "selected")}
              onClick={() => onToggle(addon)}
              aria-pressed={isSelected}
            >
              <span className="addon-body">
                <span className="addon-row">
                  <span className="addon-name">{t(addon.name)}</span>
                  <span className="addon-price">
                    {addon.listPrice > addon.price && (
                      <>
                        <span className="addon-price-regular">
                          CHF {formatCHF(addon.listPrice)}
                        </span>{" "}
                      </>
                    )}
                    CHF {formatCHF(addon.price)}
                  </span>
                </span>
                {hasDesc && <span className="addon-desc">{desc}</span>}
              </span>
            </motion.button>
          )
        })}
      </div>

      {needsCollapse && !expanded && (
        <button
          type="button"
          className="addon-expand"
          onClick={() => setExpanded(true)}
        >
          {t("gate.addons.show_more", { count: String(hiddenCount) })}
        </button>
      )}
    </div>
  )
}
